import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, Thermometer, CloudFog } from 'lucide-react';

// Weather code to icon and description mapping (WMO codes)
const getWeatherInfo = (code) => {
  const weatherMap = {
    0: { icon: Sun, description: 'Clear sky', color: 'text-yellow-400' },
    1: { icon: Sun, description: 'Mainly clear', color: 'text-yellow-400' },
    2: { icon: Cloud, description: 'Partly cloudy', color: 'text-gray-400' },
    3: { icon: Cloud, description: 'Overcast', color: 'text-gray-500' },
    45: { icon: CloudFog, description: 'Foggy', color: 'text-gray-400' },
    48: { icon: CloudFog, description: 'Icy fog', color: 'text-blue-300' },
    51: { icon: CloudRain, description: 'Light drizzle', color: 'text-blue-400' },
    53: { icon: CloudRain, description: 'Drizzle', color: 'text-blue-400' },
    55: { icon: CloudRain, description: 'Heavy drizzle', color: 'text-blue-500' },
    61: { icon: CloudRain, description: 'Light rain', color: 'text-blue-400' },
    63: { icon: CloudRain, description: 'Rain', color: 'text-blue-500' },
    65: { icon: CloudRain, description: 'Heavy rain', color: 'text-blue-600' },
    71: { icon: CloudSnow, description: 'Light snow', color: 'text-blue-200' },
    73: { icon: CloudSnow, description: 'Snow', color: 'text-blue-300' },
    75: { icon: CloudSnow, description: 'Heavy snow', color: 'text-blue-400' },
    80: { icon: CloudRain, description: 'Rain showers', color: 'text-blue-500' },
    81: { icon: CloudRain, description: 'Moderate showers', color: 'text-blue-500' },
    82: { icon: CloudRain, description: 'Heavy showers', color: 'text-blue-600' },
    95: { icon: CloudLightning, description: 'Thunderstorm', color: 'text-purple-500' },
    96: { icon: CloudLightning, description: 'Thunderstorm with hail', color: 'text-purple-600' },
    99: { icon: CloudLightning, description: 'Severe thunderstorm', color: 'text-purple-700' },
  };
  return weatherMap[code] || { icon: Cloud, description: 'Unknown', color: 'text-gray-400' };
};

const WeatherWidget = ({ latitude, longitude, eventDate, eventName }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!latitude || !longitude) {
      setError('Location not available');
      setLoading(false);
      return;
    }

    const fetchWeather = async () => {
      try {
        setLoading(true);
        
        // Parse event date to get the specific day
        const eventDateObj = new Date(eventDate);
        const today = new Date();
        const diffDays = Math.ceil((eventDateObj - today) / (1000 * 60 * 60 * 24));
        
        // Open-Meteo provides up to 16 days forecast
        if (diffDays > 16) {
          setError('Forecast not available yet (event is more than 16 days away)');
          setLoading(false);
          return;
        }

        // Fetch weather from Open-Meteo (free, no API key needed!)
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,wind_speed_10m_max&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m&timezone=Europe%2FLondon&forecast_days=16`;
        
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch weather');
        
        const data = await response.json();
        
        // Find the forecast for the event date
        const eventDateStr = eventDateObj.toISOString().split('T')[0];
        const dayIndex = data.daily.time.findIndex(d => d === eventDateStr);
        
        if (dayIndex === -1) {
          // Event is today or in the past, show current weather
          setWeather({
            isEventDay: diffDays <= 0,
            current: {
              temp: Math.round(data.current.temperature_2m),
              weatherCode: data.current.weather_code,
              windSpeed: Math.round(data.current.wind_speed_10m),
              humidity: data.current.relative_humidity_2m
            },
            forecast: null
          });
        } else {
          // Show forecast for event day
          setWeather({
            isEventDay: false,
            current: {
              temp: Math.round(data.current.temperature_2m),
              weatherCode: data.current.weather_code,
              windSpeed: Math.round(data.current.wind_speed_10m),
              humidity: data.current.relative_humidity_2m
            },
            forecast: {
              maxTemp: Math.round(data.daily.temperature_2m_max[dayIndex]),
              minTemp: Math.round(data.daily.temperature_2m_min[dayIndex]),
              weatherCode: data.daily.weather_code[dayIndex],
              precipProb: data.daily.precipitation_probability_max[dayIndex],
              windSpeed: Math.round(data.daily.wind_speed_10m_max[dayIndex])
            },
            daysAway: diffDays
          });
        }
        
        setError(null);
      } catch (err) {
        console.error('Weather fetch error:', err);
        setError('Unable to load weather');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [latitude, longitude, eventDate]);

  if (loading) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 animate-pulse" />
            <div className="space-y-2">
              <div className="h-4 w-24 bg-primary/10 rounded animate-pulse" />
              <div className="h-3 w-16 bg-primary/10 rounded animate-pulse" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Cloud className="w-8 h-8" />
            <span className="text-sm">{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!weather) return null;

  // Determine which weather to display
  const displayWeather = weather.forecast || weather.current;
  const weatherInfo = getWeatherInfo(displayWeather.weatherCode);
  const WeatherIcon = weatherInfo.icon;

  return (
    <Card className="bg-card border-border/50 overflow-hidden" data-testid="weather-widget">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center ${weatherInfo.color}`}>
              <WeatherIcon className="w-8 h-8" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {weather.forecast 
                  ? `Weather forecast for event day`
                  : 'Current weather at venue'}
              </p>
              <p className="font-semibold text-lg text-foreground">{weatherInfo.description}</p>
              {weather.daysAway && weather.daysAway > 0 && (
                <p className="text-xs text-primary">{weather.daysAway} day{weather.daysAway > 1 ? 's' : ''} away</p>
              )}
            </div>
          </div>
          
          <div className="text-right">
            {weather.forecast ? (
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {displayWeather.maxTemp}°<span className="text-lg text-muted-foreground">/{displayWeather.minTemp}°</span>
                </p>
              </div>
            ) : (
              <p className="text-3xl font-bold text-foreground">{displayWeather.temp}°C</p>
            )}
          </div>
        </div>
        
        {/* Weather details row */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-border/50">
          {displayWeather.precipProb !== undefined && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Droplets className="w-4 h-4 text-primary" />
              <span>{displayWeather.precipProb}% rain</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Wind className="w-4 h-4 text-secondary" />
            <span>{displayWeather.windSpeed} km/h</span>
          </div>
          {displayWeather.humidity !== undefined && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Droplets className="w-4 h-4 text-accent" />
              <span>{displayWeather.humidity}% humidity</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WeatherWidget;
